import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/react-router';
import { FiClock, FiCopy, FiUsers } from 'react-icons/fi';

import { client } from '../../client';
import DashboardLayout from '../layout/DashboardLayout';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Dialog,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
  Textarea,
} from '../../components/ui';
import {
  DEFAULT_WISHLIST_DESCRIPTION,
  DEFAULT_WISHLIST_FIELDS,
  DEFAULT_WISHLIST_TITLE,
} from '../../wishlist/fields';
import './index.css';

const createDefaultWishlist = (userId) => ({
  _id: `datingWishlist-${userId}`,
  title: DEFAULT_WISHLIST_TITLE,
  description: DEFAULT_WISHLIST_DESCRIPTION,
  fields: DEFAULT_WISHLIST_FIELDS,
});

const WishlistPage = () => {
  const navigate = useNavigate();
  const { isLoaded, userId } = useAuth();

  const [activeTab, setActiveTab] = useState('builder');
  const [wishlist, setWishlist] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }
    if (!userId) {
      navigate('/');
      return;
    }

    const wishlistId = `datingWishlist-${userId}`;
    client
      .createIfNotExists({
        _type: 'user',
        _id: userId,
        userId: userId,
      })
      .catch((error) => console.error('Error creating user for wishlist:', error));

    const loadWishlist = async () => {
      try {
        const data = await client.fetch('*[_type == "datingWishlist" && _id == $wishlistId][0]', {
          wishlistId,
        });

        if (data) {
          const normalizedFields = (data.fields || []).map((field) => ({
            ...field,
            required: Boolean(field.required),
          }));
          setWishlist({
            _id: data._id,
            title: data.title || DEFAULT_WISHLIST_TITLE,
            description: data.description || DEFAULT_WISHLIST_DESCRIPTION,
            fields: normalizedFields.length ? normalizedFields : DEFAULT_WISHLIST_FIELDS,
          });
          return;
        }

        const defaultWishlist = {
          _id: wishlistId,
          _type: 'datingWishlist',
          title: DEFAULT_WISHLIST_TITLE,
          description: DEFAULT_WISHLIST_DESCRIPTION,
          fields: DEFAULT_WISHLIST_FIELDS,
          isPublished: true,
          user: { _type: 'reference', _ref: userId },
        };

        await client.createIfNotExists(defaultWishlist);
        setWishlist(createDefaultWishlist(userId));
      } catch (error) {
        console.error('Failed to load wishlist:', error);
      } finally {
        setWishlistLoading(false);
      }
    };

    loadWishlist();
  }, [isLoaded, navigate, userId]);

  useEffect(() => {
    if (!wishlist?._id) {
      return;
    }
    client
      .fetch('*[_type == "datingWishlistApplication" && wishlist._ref == $wishlistId] | order(_createdAt desc)', {
        wishlistId: wishlist._id,
      })
      .then((data) => setApplications(data || []));
  }, [wishlist?._id]);

  const publicWishlistLink = useMemo(
    () => (wishlist?._id ? `${window.location.origin}/wishlist/${encodeURIComponent(wishlist._id)}` : ''),
    [wishlist?._id]
  );
  const newestApplication = useMemo(
    () => (applications.length ? new Date(applications[0]._createdAt).toLocaleDateString() : 'N/A'),
    [applications]
  );
  const handleCopyLink = async () => {
    if (!publicWishlistLink) {
      return;
    }
    try {
      await navigator.clipboard.writeText(publicWishlistLink);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 1800);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleFieldRequiredToggle = (fieldKey, checked) => {
    setWishlist((prev) => ({
      ...prev,
      fields: prev.fields.map((field) => (field.key === fieldKey ? { ...field, required: checked } : field)),
    }));
  };

  const handleSaveWishlist = async () => {
    if (!wishlist || !userId) {
      return;
    }
    setLoading(true);

    const nextWishlist = {
      _id: wishlist._id,
      _type: 'datingWishlist',
      title: wishlist.title,
      description: wishlist.description,
      fields: wishlist.fields,
      isPublished: true,
      user: { _type: 'reference', _ref: userId },
    };

    try {
      await client.createOrReplace(nextWishlist);
    } catch (error) {
      console.error('Failed to save wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  if (wishlistLoading) {
    return (
      <DashboardLayout title="Your Dating Waitlist" subtitle="Getting your vibe board ready…">
        <div className="wishlist-page">
          <Card>
            <CardContent>
              <p className="ui-muted">Loading...</p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Your Dating Waitlist"
      subtitle="Set your standards, share your link, and see who's raising their hand."
      actions={
        <Button className="wishlist-save-btn" onClick={handleSaveWishlist} disabled={loading}>
          {loading ? 'Saving…' : 'Save & publish'}
        </Button>
      }
    >
      <div className="wishlist-page">
      <Tabs className="wishlist-tabs">
        <TabsList>
          <TabsTrigger active={activeTab === 'builder'} onClick={() => setActiveTab('builder')}>
            Your vibe
          </TabsTrigger>
          <TabsTrigger active={activeTab === 'applicants'} onClick={() => setActiveTab('applicants')}>
            Interested ({applications.length})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {activeTab === 'builder' ? (
        <div className="wishlist-grid">
          <Card>
            <CardHeader>
              <CardTitle>What you're looking for</CardTitle>
              <CardDescription>Paint the picture — the right people will know they're invited.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="ui-grid">
                <div>
                  <Label htmlFor="wishlist-title">Title</Label>
                  <Input
                    id="wishlist-title"
                    value={wishlist.title}
                    onChange={(event) => setWishlist((prev) => ({ ...prev, title: event.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="wishlist-description">Description</Label>
                  <Textarea
                    id="wishlist-description"
                    value={wishlist.description}
                    onChange={(event) => setWishlist((prev) => ({ ...prev, description: event.target.value }))}
                  />
                </div>
                <div>
                  <Label>Your share link</Label>
                  <div className="wishlist-public-link">
                    <a href={publicWishlistLink} target="_blank" rel="noreferrer" className="ui-link">
                      {publicWishlistLink}
                    </a>
                    <Button type="button" size="sm" variant="secondary" onClick={handleCopyLink}>
                      <FiCopy />
                      {copiedLink ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Deal-breakers & must-haves</CardTitle>
              <CardDescription>Mark what they can't skip — honesty starts here.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="ui-grid">
                {wishlist.fields.map((field) => (
                  <div key={field.key} className="wishlist-field-row">
                    <div>
                      <p className="wishlist-field-title">{field.label}</p>
                      <p className="ui-muted">
                        {field.type === 'multi' ? 'Multiple choice' : field.type}
                        {field.options?.length ? ` - ${field.options.length} options` : ''}
                      </p>
                    </div>
                    <Checkbox
                      id={`required-${field.key}`}
                      checked={field.required}
                      onChange={(event) => handleFieldRequiredToggle(field.key, event.target.checked)}
                      label="Required"
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="applications-review-grid">
          <section className="applications-review-stats">
            <Card>
              <CardContent>
                <div className="applications-stat-card">
                  <FiUsers />
                  <div>
                    <p>Raised their hand</p>
                    <h3>{applications.length}</h3>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <div className="applications-stat-card">
                  <FiClock />
                  <div>
                    <p>Most recent</p>
                    <h3>{newestApplication}</h3>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          <Card>
            <CardHeader>
              <CardTitle>Who's interested</CardTitle>
              <CardDescription>Take a closer look at everyone who applied — no awkward small talk required.</CardDescription>
            </CardHeader>
            <CardContent>
              {applications.length === 0 ? (
                <div className="wishlist-empty-state">
                  <p>
                    <strong>Your inbox is quiet — for now.</strong>
                    Drop your share link where the right people hang out. When someone applies, they'll show up here.
                  </p>
                </div>
              ) : (
                <TableContainer className="applications-table-wrap">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Applicant</TableHead>
                        <TableHead>Goal</TableHead>
                        <TableHead>Submitted</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {applications.map((application) => {
                        const applicantName = application.answers?.name || 'Unknown';
                        const initial = applicantName?.charAt(0)?.toUpperCase() || '?';
                        return (
                          <TableRow key={application._id}>
                            <TableCell>
                              <div className="applicant-cell">
                                <span className="applicant-avatar">{initial}</span>
                                <div>
                                  <p>{applicantName}</p>
                                  <span className="ui-muted">ID: {application._id.slice(-6)}</span>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              {application.answers?.relationshipGoal ? (
                                <Badge className="wishlist-goal-badge" variant="secondary">
                                  {application.answers.relationshipGoal}
                                </Badge>
                              ) : (
                                <Badge variant="outline">Not provided</Badge>
                              )}
                            </TableCell>
                            <TableCell>{new Date(application._createdAt).toLocaleString()}</TableCell>
                            <TableCell>
                              <Button
                                className="wishlist-open-review-btn"
                                size="sm"
                                variant="outline"
                                onClick={() => setSelectedApplication(application)}
                              >
                                Take a look
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog
        open={Boolean(selectedApplication)}
        onOpenChange={() => setSelectedApplication(null)}
        title={selectedApplication?.answers?.name || 'Applicant Details'}
        description="Everything they shared — read it with an open mind."
      >
        {selectedApplication ? (
          <div className="application-review-header">
            <Badge variant="outline">{new Date(selectedApplication._createdAt).toLocaleString()}</Badge>
            <p className="ui-muted">Application ID: {selectedApplication._id}</p>
          </div>
        ) : null}
        {selectedApplication ? (
          <div className="application-review-grid">
            {wishlist.fields.map((field) => {
              const answer = selectedApplication.answers?.[field.key];
              const displayValue = Array.isArray(answer) ? answer.join(', ') : answer || 'Not answered';
              return (
                <div key={field.key} className="application-answer-card">
                  <p className="wishlist-field-title">{field.label}</p>
                  <p className="application-answer-value">{displayValue}</p>
                  {field.required ? <Badge className="wishlist-required-badge">Required</Badge> : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default WishlistPage;